import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Bell,
  Check,
  CheckCheck,
  FileImage,
  FileText,
  Inbox,
  MessageSquare,
  Paperclip,
  Search,
  Send,
  X,
} from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle } from "@/components/Shared";

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function attachmentIcon(mime = "") {
  return mime.startsWith("image/") ? FileImage : FileText;
}

export default function Broadcast() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSend = ["SUPER_ADMIN", "KORWIL", "ADMIN_KORWIL"].includes(user?.role);
  const fileRef = useRef(null);

  const [items, setItems] = useState([]);
  const [options, setOptions] = useState([]);
  const [types, setTypes] = useState([]);
  const [transactionOptions, setTransactionOptions] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [transactionSearch, setTransactionSearch] = useState("");
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [compose, setCompose] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    judul: "",
    jenis: "Pengumuman",
    pesan: "",
    transaction_ids: [],
    recipients: [],
    file: null,
  });

  const unread = useMemo(
    () => canSend ? 0 : items.filter((x) => !x.is_read).length,
    [items, canSend]
  );

  const load = async () => {
    setLoading(true);
    try {
      const requests = [
        api.get("/broadcast/messages"),
        api.get("/broadcast/options"),
      ];
      if (canSend) requests.push(api.get("/broadcast/transactions"));
      const results = await Promise.all(requests);
      const messages = results[0].data;
      const opt = results[1].data;
      const tx = results[2]?.data;

      setItems(messages.items || []);
      setOptions(opt.items || []);
      setTypes(opt.types || []);
      if (canSend) setTransactionOptions(tx?.items || []);
    } catch {
      toast.error("Gagal memuat pesan");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!compose) return;
    const timer = setTimeout(async () => {
      setTransactionLoading(true);
      try {
        const { data } = await api.get("/broadcast/transactions", { params: { search: transactionSearch, limit: 100 } });
        setTransactionOptions(data.items || []);
      } catch {
        toast.error("Gagal memuat daftar transaksi");
      } finally {
        setTransactionLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [compose, transactionSearch]);

  const toggleRecipient = (id) => {
    setForm((f) => ({
      ...f,
      recipients: f.recipients.includes(id)
        ? f.recipients.filter((x) => x !== id)
        : [...f.recipients, id],
    }));
  };

  const selectAll = () => {
    setForm((f) => ({
      ...f,
      recipients: f.recipients.length === options.length ? [] : options.map((x) => x.id),
    }));
  };

  const toggleTransaction = (id) => {
    setForm((f) => ({
      ...f,
      transaction_ids: f.transaction_ids.includes(id)
        ? f.transaction_ids.filter((x) => x !== id)
        : [...f.transaction_ids, id],
    }));
  };

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const filteredRecipients = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    if (!q) return options;
    return options.filter((x) =>
      [x.korda, x.name, x.email, x.korwil, x.role].some((v) =>
        String(v || "").toLowerCase().includes(q)
      )
    );
  }, [options, recipientSearch]);

  const send = async (e) => {
    e.preventDefault();
    if (!form.judul.trim() || !form.pesan.trim()) {
      toast.error("Judul dan pesan wajib diisi");
      return;
    }
    if (!form.recipients.length) {
      toast.error("Pilih minimal satu KORDA");
      return;
    }

    const fd = new FormData();
    fd.append("judul", form.judul.trim());
    fd.append("jenis", form.jenis);
    fd.append("pesan", form.pesan.trim());
    fd.append("transaction_ids", form.transaction_ids.join(","));
    fd.append("recipients", form.recipients.join(","));
    if (form.file) fd.append("file", form.file);

    setBusy(true);
    try {
      await api.post("/broadcast/messages", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Pesan berhasil dikirim");
      setCompose(false);
      setForm({
        judul: "",
        jenis: types[0] || "Pengumuman",
        pesan: "",
        transaction_ids: [],
        recipients: [],
        file: null,
      });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim pesan");
    } finally {
      setBusy(false);
    }
  };

  const openMessage = async (item) => {
    setSelected(item);
    if (!item.is_read && user?.role === "KORDA") {
      try {
        await api.post(`/broadcast/messages/${item.id}/read`);
        setItems((rows) =>
          rows.map((x) => x.id === item.id ? { ...x, is_read: true } : x)
        );
      } catch {}
    }
  };

  const downloadAttachment = (item) => {
    window.open(`/api/broadcast/messages/${item.id}/attachment`, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="page-broadcast">
      <SectionTitle
        title="Broadcast & Pesan"
        subtitle={
          canSend
            ? "Kirim informasi, permintaan revisi, dan pengingat langsung ke KORDA."
            : "Pesan dan instruksi dari Korwil / Admin Korwil."
        }
        right={
          canSend && (
            <button
              onClick={() => setCompose(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
            >
              <Send className="h-4 w-4" />
              Buat Pesan
            </button>
          )
        }
      />

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10 text-blue-300">
              <Inbox className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500">Total Pesan</div>
              <div className="font-display text-xl font-bold text-white">{items.length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-500/10 text-red-300">
              <Bell className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500">Belum Dibaca</div>
              <div className="font-display text-xl font-bold text-red-300">{unread}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-300">
              <MessageSquare className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500">Lampiran</div>
              <div className="font-display text-xl font-bold text-white">
                {items.filter((x) => x.attachment).length}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <div className="border-b border-slate-800 bg-slate-900/50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
          {canSend ? "Pesan Terkirim" : "Kotak Masuk"}
        </div>
        <div className="max-h-[calc(100vh-360px)] overflow-auto">
          {loading && (
            <div className="p-12 text-center text-sm text-slate-500">Memuat pesan...</div>
          )}

          {!loading && items.length === 0 && (
            <div className="p-16 text-center">
              <MessageSquare className="mx-auto mb-3 h-9 w-9 text-slate-600" />
              <div className="text-sm font-semibold text-slate-400">
                {canSend ? "Belum ada pesan terkirim." : "Belum ada pesan masuk."}
              </div>
            </div>
          )}

          {!loading && items.map((item) => (
            <button
              key={item.id}
              onClick={() => openMessage(item)}
              className="flex w-full items-start gap-4 border-b border-slate-800/80 px-5 py-4 text-left hover:bg-slate-900/70"
            >
              <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${item.is_read ? "bg-slate-800 text-slate-400" : "bg-blue-500/15 text-blue-300"}`}>
                <MessageSquare className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {!item.is_read && <span className="h-2 w-2 rounded-full bg-red-500" />}
                  <span className={`font-semibold ${item.is_read ? "text-slate-300" : "text-white"}`}>{item.judul}</span>
                  <span className="rounded-full border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400">{item.jenis}</span>
                </div>
                <div className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{item.pesan}</div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
                  <span>{canSend ? `Diterima ${item.recipient_count} KORDA` : `Dari: ${item.sender_name}`}</span>
                  {canSend && <span>{item.read_count}/{item.recipient_count} sudah dibaca</span>}
                  {item.attachment && <span className="inline-flex items-center gap-1"><Paperclip className="h-3 w-3" /> Lampiran</span>}
                  <span>{formatDate(item.created_at)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-800 px-5 py-4">
              <div className="min-w-0">
                <div className="mb-1 flex items-center gap-2">
                  <span className="rounded-full border border-blue-500/30 bg-blue-500/10 px-2.5 py-1 text-[10px] font-medium text-blue-300">{selected.jenis}</span>
                  {(selected.transaction_ids?.length || selected.transaction_id) > 0 && (
                    <span className="font-mono-data text-[10px] text-blue-300">
                      {(selected.transaction_ids?.length ? selected.transaction_ids : [selected.transaction_id]).join(" · ")}
                    </span>
                  )}
                </div>
                <h2 className="font-display text-lg font-semibold text-white">{selected.judul}</h2>
                <div className="mt-1 text-xs text-slate-500">Dari {selected.sender_name} · {formatDate(selected.created_at)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-auto p-5">
              <div className="whitespace-pre-wrap rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-sm leading-6 text-slate-200">
                {selected.pesan}
              </div>

              {(selected.transaction_ids?.length || selected.transaction_id) > 0 && (
                <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-200">
                  <div className="mb-2 font-semibold text-slate-900">Transaksi terkait</div>
                  <div className="flex flex-wrap gap-2">
                    {(selected.transaction_ids?.length ? selected.transaction_ids : [selected.transaction_id]).map((id) => (
                      <button
                        key={id}
                        type="button"
                        title="Buka detail transaksi di Perlu Tindakan"
                        onClick={() => {
                          setSelected(null);
                          navigate(`/attention?id=${encodeURIComponent(id)}`);
                        }}
                        className="rounded-md border border-blue-500/30 bg-blue-500/10 px-2 py-1 font-mono-data text-blue-300 underline decoration-blue-500/40 underline-offset-2 transition-colors hover:border-blue-400 hover:bg-blue-500/20 hover:text-blue-200"
                      >
                        {id}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selected.attachment && (
                <button
                  onClick={() => downloadAttachment(selected)}
                  className="mt-4 flex w-full items-center gap-3 rounded-xl border border-slate-700 bg-slate-900 p-3 text-left hover:border-blue-500/50"
                >
                  {(() => {
                    const Icon = attachmentIcon(selected.attachment.mime_type);
                    return <Icon className="h-5 w-5 text-blue-300" />;
                  })()}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-white">{selected.attachment.filename}</div>
                    <div className="text-xs text-slate-500">{Math.round((selected.attachment.size || 0) / 1024)} KB · Klik untuk download</div>
                  </div>
                  <Paperclip className="h-4 w-4 text-slate-500" />
                </button>
              )}

              {canSend && (
                <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                  <CheckCheck className="h-4 w-4" />
                  {selected.read_count} dari {selected.recipient_count} KORDA sudah membaca pesan
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {compose && canSend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">
            <div className="flex shrink-0 items-center justify-between border-b border-slate-800 px-5 py-4">
              <div>
                <h2 className="font-display text-lg font-semibold text-white">Buat Pesan</h2>
                <p className="mt-1 text-xs text-slate-500">
  {isSuperAdmin
    ? "Super Admin dapat mengirim pesan ke seluruh KORWIL dan KORDA yang aktif."
    : "Pesan hanya dapat dikirim ke KORDA dalam scope Korwil Anda."}
</p>
              </div>
              <button onClick={() => setCompose(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={send} className="min-h-0 overflow-auto p-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-300">Jenis Pesan</label>
                  <select
                    value={form.jenis}
                    onChange={(e) => setForm({ ...form, jenis: e.target.value })}
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                  >
                    {types.map((x) => <option key={x}>{x}</option>)}
                  </select>
                </div>
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-xs font-medium text-slate-300">ID Transaksi (opsional)</label>
                    <span className="text-[11px] text-slate-500">{form.transaction_ids.length} dipilih</span>
                  </div>
                  <div className="overflow-hidden rounded-lg border border-slate-700 bg-slate-900">
                    <div className="flex items-center gap-2 border-b border-slate-800 px-3">
                      <Search className="h-4 w-4 text-slate-500" />
                      <input value={transactionSearch} onChange={(e) => setTransactionSearch(e.target.value)} placeholder="Cari ID transaksi, sekolah, KORDA..." className="w-full bg-transparent py-2.5 text-sm text-white outline-none" />
                    </div>
                    {form.transaction_ids.length > 0 && (
                      <div className="flex max-h-20 flex-wrap gap-1.5 overflow-auto border-b border-slate-800 p-2">
                        {form.transaction_ids.map((id) => (
                          <button key={id} type="button" onClick={() => toggleTransaction(id)} className="rounded-md border border-blue-500/30 bg-blue-500/10 px-2 py-1 font-mono-data text-[10px] text-blue-300">{id} ×</button>
                        ))}
                      </div>
                    )}
                    <div className="max-h-40 overflow-auto">
                      {transactionLoading ? <div className="p-4 text-xs text-slate-500">Memuat transaksi...</div> : transactionOptions.length ? transactionOptions.map((x) => (
                        <label key={x.id} className="flex cursor-pointer items-start gap-3 border-b border-slate-800 px-3 py-2.5 last:border-0 hover:bg-slate-800/60">
                          <input type="checkbox" checked={form.transaction_ids.includes(x.id_transaksi)} onChange={() => toggleTransaction(x.id_transaksi)} className="mt-0.5 h-4 w-4 accent-blue-600" />
                          <div className="min-w-0 flex-1">
                            <div className="font-mono-data text-xs font-medium text-white">{x.id_transaksi}</div>
                            <div className="text-[11px] text-slate-500">{x.sekolah || "-"} · {x.korda || "-"}{x.kabupaten ? ` · ${x.kabupaten}` : ""}</div>
                          </div>
                        </label>
                      )) : <div className="p-4 text-xs text-slate-500">Transaksi tidak ditemukan dalam scope.</div>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="mb-1.5 block text-xs font-medium text-slate-300">Judul</label>
                <input
                  value={form.judul}
                  onChange={(e) => setForm({ ...form, judul: e.target.value })}
                  placeholder="Contoh: Revisi Foto Instalasi"
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                />
              </div>

              <div className="mt-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-300">
  {isSuperAdmin ? "Penerima KORWIL & KORDA" : "Penerima KORDA"}
</label>
                  <button type="button" onClick={selectAll} className="text-xs text-blue-300 hover:text-blue-200">
                    {form.recipients.length === options.length ? "Batal Pilih Semua" : "Pilih Semua"}
                  </button>
                </div>
                <div className="overflow-hidden rounded-lg border border-slate-700 bg-slate-900">
                  <div className="flex items-center gap-2 border-b border-slate-800 px-3">
                    <Search className="h-4 w-4 text-slate-500" />
                    <input value={recipientSearch} onChange={(e) => setRecipientSearch(e.target.value)} placeholder={isSuperAdmin ? "Cari KORWIL, KORDA, nama, atau email..." : "Cari nama atau KORDA..."} className="w-full bg-transparent py-2.5 text-sm text-white outline-none" />
                  </div>
                  <div className="max-h-44 overflow-auto">
                    {filteredRecipients.map((x) => (
                      <label key={x.id} className="flex cursor-pointer items-center gap-3 border-b border-slate-800 px-3 py-2.5 last:border-0 hover:bg-slate-800/60">
                        <input type="checkbox" checked={form.recipients.includes(x.id)} onChange={() => toggleRecipient(x.id)} className="h-4 w-4 accent-blue-600" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-white">
                              {x.korda || x.korwil || x.name || "User"}
                            </div>
                            {isSuperAdmin && (
                              <span className="rounded-full border border-slate-700 px-1.5 py-0.5 text-[9px] font-semibold text-slate-400">
                                {x.role === "KORDA" ? "KORDA" : "KORWIL"}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {x.name}{x.korwil ? ` · ${x.korwil}` : ""}{x.email ? ` · ${x.email}` : ""}
                          </div>
                        </div>
                      </label>
                    ))}
                    {!filteredRecipients.length && <div className="p-4 text-xs text-slate-500">KORDA tidak ditemukan.</div>}
                  </div>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
  {form.recipients.length} {isSuperAdmin ? "penerima" : "KORDA"} dipilih
</div>
              </div>

              <div className="mt-4">
                <label className="mb-1.5 block text-xs font-medium text-slate-300">Pesan</label>
                <textarea
                  rows={6}
                  value={form.pesan}
                  onChange={(e) => setForm({ ...form, pesan: e.target.value })}
                  placeholder="Tulis instruksi atau informasi untuk KORDA..."
                  className="w-full resize-none rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm leading-6 text-white outline-none focus:border-blue-500"
                />
              </div>

              <div className="mt-4">
                <label className="mb-1.5 block text-xs font-medium text-slate-300">Lampiran · maksimal 10 MB</label>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileRef}
                    type="file"
                    onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                    className="block w-full rounded-lg border border-dashed border-slate-700 bg-slate-900 p-3 text-xs text-slate-400 file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white"
                  />
                  {form.file && <Paperclip className="h-4 w-4 shrink-0 text-blue-300" />}
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button type="button" onClick={() => setCompose(false)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800">
                  Batal
                </button>
                <button disabled={busy} type="submit" className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
                  <Send className="h-4 w-4" />
                  {busy ? "Mengirim..." : "Kirim Pesan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
